import React from "react";
import Lottie from "lottie-react";
import FlymyaLoading from "../../lotties/loading_animation.json";
import PassengerCountIcon from "../../assets/images/pass-count.webp";

/** The separator between the summary's fields, as used in the results header. */
const SummaryDot = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="3"
    height="3"
    viewBox="0 0 3 3"
    fill="none"
    aria-hidden="true"
  >
    <circle cx="1.5" cy="1.5" r="1.5" fill="#232323" />
  </svg>
);

const OneWayArrow = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="8"
    viewBox="0 0 16 8"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M15.3536 4.03556C15.5488 3.8403 15.5488 3.52372 15.3536 3.32845L12.1716 0.146473C11.9763 -0.0487893 11.6597 -0.0487893 11.4645 0.146473C11.2692 0.341735 11.2692 0.658318 11.4645 0.85358L14.2929 3.68201L11.4645 6.51043C11.2692 6.7057 11.2692 7.02228 11.4645 7.21754C11.6597 7.4128 11.9763 7.4128 12.1716 7.21754L15.3536 4.03556ZM0 3.68201V4.18201H15V3.68201V3.18201H0V3.68201Z"
      fill="black"
    />
  </svg>
);

const RoundTripArrows = () => (
  <span className="flex flex-col" aria-hidden="true">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="10"
      height="8"
      viewBox="0 0 10 8"
      fill="none"
    >
      <path
        d="M9.35355 4.03519C9.54882 3.83993 9.54882 3.52335 9.35355 3.32809L6.17157 0.146107C5.97631 -0.049155 5.65973 -0.049155 5.46447 0.146107C5.2692 0.341369 5.2692 0.657952 5.46447 0.853214L8.29289 3.68164L5.46447 6.51007C5.2692 6.70533 5.2692 7.02191 5.46447 7.21718C5.65973 7.41244 5.97631 7.41244 6.17157 7.21718L9.35355 4.03519ZM0 3.68164L-4.37114e-08 4.18164L9 4.18164L9 3.68164L9 3.18164L4.37114e-08 3.18164L0 3.68164Z"
        fill="black"
      />
    </svg>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="10"
      height="8"
      viewBox="0 0 10 8"
      fill="none"
    >
      <path
        d="M0.146446 4.03519C-0.0488157 3.83993 -0.0488157 3.52335 0.146446 3.32809L3.32843 0.146107C3.52369 -0.049155 3.84027 -0.049155 4.03553 0.146107C4.2308 0.341369 4.2308 0.657952 4.03553 0.853214L1.20711 3.68164L4.03553 6.51007C4.2308 6.70533 4.2308 7.02191 4.03553 7.21718C3.84027 7.41244 3.52369 7.41244 3.32843 7.21718L0.146446 4.03519ZM9.5 3.68164L9.5 4.18164L0.5 4.18164L0.5 3.68164L0.5 3.18164L9.5 3.18164L9.5 3.68164Z"
        fill="black"
      />
    </svg>
  </span>
);

/**
 * Blocks the results while a search runs.
 *
 * The list fills in operator by operator, so it is readable long before the
 * search is done; without this the only cue was the skeleton, which stops
 * rendering as soon as the first rows land. The caller holds `open` true until
 * every operator request has settled.
 */
const FlightSearchLoadingModal = ({
  open,
  fromLabel,
  toLabel,
  isRoundTrip = false,
  dateLabel,
  passengerCount,
  tripTypeLabel,
  cabinClassLabel,
  message = "Searching the best deals...",
}) => {
  if (!open) return null;

  return (
    <div
      // Above MUI's modal layer so a stray dialog cannot sit on top of it.
      className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/50 px-[20px] max-780:px-[10px]"
      role="status"
      aria-live="polite"
    >
      <div className="w-full max-w-[350px] max-h-[150px] rounded-[16px] bg-white px-[10px] py-[20px] text-center shadow-[0_4px_20px_rgba(0,0,0,0.15)] max-780:max-w-[440px] max-780:max-h-none max-780:px-[12px]">
        <div className="flex flex-wrap items-center justify-center gap-[7px] font-roboto text-[18px] font-[500] text-[#231f20] max-780:gap-[10px] max-780:text-[18px]">
          <span className="font-[500] text-[18px]">{fromLabel}</span>
          {isRoundTrip ? <RoundTripArrows /> : <OneWayArrow />}
          <span className="font-[500] text-[18px]">{toLabel}</span>
        </div>

        <div className="flex flex-wrap items-center my-[2px] justify-center gap-[3px] font-roboto text-[12px] font-[400] font-normal text-[#00AEEF] max-780:text-[13px]">
          <span>{dateLabel}</span>
          <SummaryDot />
          <span className="flex items-center gap-[5px]">
            {passengerCount}
            <img
              src={PassengerCountIcon}
              alt="passenger"
              className="h-[12px] w-[12px]"
              width={12}
              height={12}
            />
          </span>
          <SummaryDot />
          <span>{tripTypeLabel}</span>
          <SummaryDot />
          <span>{cabinClassLabel}</span>
        </div>

        <div className="flex justify-center">
          {/* The same animation FlymyaIconLoading plays, at card scale rather
              than full screen. */}
          <Lottie
            animationData={FlymyaLoading}
            loop={true}
            className="h-[40px] w-[40px]"
          />
        </div>

        <p className="m-0 font-roboto text-[12px] font-[400] mt-[2px] font-normal text-[#001118] max-780:text-[14px]">
          {message}
        </p>
      </div>
    </div>
  );
};

export default FlightSearchLoadingModal;
